import type { Enterprise } from '@/modules/enterprise/types/enterprise.interface';
import { useStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { readonly, ref } from 'vue';
import {
  changeRegisteredEmailService,
  checkFirstStepsCompletedService,
  fetchUserTotalRemainingCreditsService,
  recoverPasswordService,
  resendEmailVerificationTokenService,
  updatePasswordService,
  updatePersonalInfoService,
  validateEmailVerificationTokenService,
} from '../services/user.service';
import type { FirstSteps } from '../types/firstSteps.interface';
import type { UpdatePasswordForm } from '../types/updatePassword.interface';
import type { UpdatePersonalInfoForm } from '../types/updatePersonalInfo.interface';
import type { User } from '../types/user.interface';
import { USER_DATA_TTL } from '../types/userCache.interface';

export const useUserStore = defineStore('user', () => {
  // sincronizado con localStorage
  const user = useStorage<User>('user', null, localStorage, {
    serializer: {
      read: v => JSON.parse(v),
      write: v => JSON.stringify(v),
    },
  });

  const totalRemainingCredits = ref<number | null>(null);

  /**
   * @author: Omar Sánchez Martinez
   * @since: 18/06/2025
   * @description: Reset personalizado del store de usuario que maneja correctamente useStorage y readonly refs
   */
  const resetCustomStore = () => {
    try {
      user.value = null;
      totalRemainingCredits.value = null;
    } catch (error) {
      console.warn('Error resetting user store:', error);
    }
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 30/05/2025
   * @description: Establece el valor user en la reactividad de user
   * @updated: 10/02/2026 - Agregado soporte para metadata de cache
   */
  const setUser = (userData: User) => {
    // Si no tiene metadata, agregarla (para compatibilidad con login y migración)
    if (!('metadata' in userData)) {
      const userWithMetadata = {
        ...userData,
        metadata: {
          lastFetched: Date.now(),
          ttl: USER_DATA_TTL,
        },
      };
      user.value = userWithMetadata;
    } else {
      user.value = userData;
    }
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 30/05/2025
   * @description: Limpia el valor user de localStorage y la reactividad de user
   */
  const clearUser = () => {
    user.value = null;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Verifica si los pasos iniciales del usuario han sido completados
   */
  const checkFirstStepsCompleted = async (): Promise<FirstSteps> => {
    const steps = await checkFirstStepsCompletedService();

    if (
      steps.client_created &&
      steps.product_service_created &&
      steps.sellos_digitales_created &&
      steps.invoice_created
    ) {
      if (user.value) {
        user.value.first_steps_completed = true;
      }
    }

    return steps;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 26/05/2025
   * @description: Obtiene el total de créditos restantes del usuario
   */
  const fetchUserTotalRemainingCredits = async () => {
    totalRemainingCredits.value = await fetchUserTotalRemainingCreditsService();
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 15/09/2025
   * @description: Decrementa en 1 los créditos restantes del usuario de forma local
   *               Se usa tras un timbrado exitoso. Nunca permite valores negativos.
   */
  const decrementRemainingCredits = (): void => {
    if (typeof totalRemainingCredits.value === 'number') {
      totalRemainingCredits.value = Math.max(0, totalRemainingCredits.value - 1);
    }
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 30/05/2025
   * @description: Recupera la contraseña del usuario
   */
  const recoverPassword = async (email: string) => {
    const success = await recoverPasswordService(email);
    if (!success) {
      throw new Error('Error al recuperar la contraseña');
    }
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 01/06/2025
   * @description: Actualiza la contraseña del usuario
   */
  const updatePassword = async (updatePasswordForm: UpdatePasswordForm): Promise<boolean> => {
    return await updatePasswordService(updatePasswordForm);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 23/06/2025
   * @description: Cambia el email registrado del usuario
   */
  const changeRegisteredEmail = async (email: string): Promise<boolean> => {
    return await changeRegisteredEmailService(email);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 23/06/2025
   * @description: Reenvía el token de verificación de email
   */
  const resendEmailVerificationToken = async (email: string): Promise<boolean> => {
    return await resendEmailVerificationTokenService(email);
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 23/06/2025
   * @description: Valida el token de verificación de email y actualiza el email del usuario
   * @throws {ApiError} Si el token es inválido o hay un error del servidor
   */
  const validateEmailVerificationToken = async (token: string, email: string): Promise<boolean> => {
    const success = await validateEmailVerificationTokenService(token, email);

    if (success && user.value) {
      user.value.email = email;
    }

    return success;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 22/06/2025
   * @description: Actualiza la información personal del usuario
   */
  const updatePersonalInfo = async (
    updatePersonalInfoForm: UpdatePersonalInfoForm,
  ): Promise<boolean> => {
    const success = await updatePersonalInfoService(updatePersonalInfoForm);

    if (success && user.value) {
      // Update user data in store after successful update
      user.value.name = updatePersonalInfoForm.name;
      user.value.phone = updatePersonalInfoForm.phone;
    }

    return success;
  };

  /**
   * @author: Omar Sánchez Martinez
   * @since: 22/08/2025
   * @description: Actualiza una empresa específica en el arreglo de empresas del usuario
   */
  const updateUserEnterprise = (enterpriseId: number, updatedEnterprise: Enterprise): void => {
    if (!user.value || !user.value.enterprises) {
      return;
    }

    const enterpriseIndex = user.value.enterprises.findIndex(
      enterprise => enterprise.id === enterpriseId,
    );

    if (enterpriseIndex !== -1) {
      // Update the enterprise with new data while preserving existing fields
      user.value.enterprises[enterpriseIndex] = {
        ...user.value.enterprises[enterpriseIndex],
        ...updatedEnterprise,
      };
    }
  };

  return {
    // Properties
    user,
    totalRemainingCredits: readonly(totalRemainingCredits),

    // Actions
    setUser,
    clearUser,
    checkFirstStepsCompleted,
    fetchUserTotalRemainingCredits,
    decrementRemainingCredits,
    recoverPassword,
    updatePassword,
    updatePersonalInfo,
    changeRegisteredEmail,
    resendEmailVerificationToken,
    validateEmailVerificationToken,
    updateUserEnterprise,
    resetCustomStore,
  };
});
